import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50
  }
};

@Component({
  selector: 'app-add-offramp-address-mapping',
  templateUrl: './add-offramp-address-mapping.component.html',
  styleUrls: ['./add-offramp-address-mapping.component.css']
})
export class AddOfframpAddressMappingDialog implements OnInit {
  element: any = {};
  myForm: FormGroup;
  customerList: any[] = [];
  filteredCustomerList: any[] = [];
  userSearchValue: string = '';
  isUserSelectOpen: boolean = false;
  isEditMode: boolean = false;
  recordId: string = '';

  constructor(
    public dialogRef: MatDialogRef<AddOfframpAddressMappingDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private notifSer: NotificationsService,
    public fb: FormBuilder,
    public userService: UserService,
    public offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) {
    this.element = data.record || {};
    this.isEditMode = !!this.element._id;
    this.recordId = this.element._id || '';

    const loggedInUser = this.userService.getUserDetails();

    this.myForm = this.fb.group({
      adminid: [
        this.element.adminid ? (this.element.adminid._id || this.element.adminid) : (loggedInUser ? loggedInUser._id : ''),
        [Validators.required]
      ],
      userid: [
        this.element.userid ? (this.element.userid._id || this.element.userid) : '',
        [Validators.required]
      ],
      network: [this.element.network || '', [Validators.required]],
      currency: [this.element.currency || '', [Validators.required]],
      from_address_name: [this.element.from_address_name || '', [Validators.required]],
      from_address: [this.element.from_address || '', [Validators.required]],
      to_address_name: [this.element.to_address_name || '', [Validators.required]],
      to_address: [this.element.to_address || '', [Validators.required]]
    });
  }

  ngOnInit() {
    this.loadCustomerList();
  }

  loadCustomerList() {
    this.spinnerService.show();
    const filter = {
      _csrf: this.data._csrf,
      perpage: 10000,
      page: 1,
      search: '',
      status: 'Active',
    };

    this.offrampService.getOfframpCustomerList(filter).subscribe(
      (response: any) => {
        this.spinnerService.hide();
        if (response && response.customers && Array.isArray(response.customers)) {
          this.customerList = response.customers;
        } else if (response && Array.isArray(response)) {
          this.customerList = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          this.customerList = response.data;
        } else {
          this.customerList = [];
          this.notifSer.warn('Warning', 'No customers found', defaults.notif);
        }

        const currentUserId = this.myForm.get('userid').value;
        if (currentUserId && !this.customerList.some((u) => u._id === currentUserId) && this.element.userid && typeof this.element.userid === 'object') {
          this.customerList = [this.element.userid, ...this.customerList];
        }

        this.filteredCustomerList = this.customerList;
      },
      () => {
        this.spinnerService.hide();
        this.notifSer.error('Error', 'Failed to load customer list', defaults.notif);
        this.customerList = [];
        this.filteredCustomerList = [];
      }
    );
  }

  onUserSelectOpened(isOpen: boolean) {
    this.isUserSelectOpen = isOpen;
    if (isOpen) {
      this.userSearchValue = '';
      this.filteredCustomerList = this.customerList;
      setTimeout(() => {
        this.addSearchToSelectPanel();
      }, 150);
    } else {
      this.userSearchValue = '';
      this.filteredCustomerList = this.customerList;
    }
  }

  addSearchToSelectPanel() {
    const panel = document.querySelector('.user-select-panel-address-mapping');
    if (panel) {
      const existingSearch = panel.querySelector('.user-search-container');
      if (existingSearch) {
        existingSearch.remove();
      }

      const searchContainer = document.createElement('div');
      searchContainer.className = 'user-search-container';
      searchContainer.innerHTML = `
        <input type="text" placeholder="Search user..." class="user-search-input" value="${this.userSearchValue || ''}">
      `;

      const searchInput = searchContainer.querySelector('.user-search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.addEventListener('input', (e: any) => {
          this.userSearchValue = e.target.value;
          this.filterUsers(e);
        });
        searchInput.addEventListener('click', (e: any) => {
          e.stopPropagation();
        });
        searchInput.addEventListener('keydown', (e: any) => {
          e.stopPropagation();
        });
      }

      if (panel.firstChild) {
        panel.insertBefore(searchContainer, panel.firstChild);
      } else {
        panel.appendChild(searchContainer);
      }

      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 50);
      }
    }
  }

  filterUsers(event: any) {
    const searchTerm = event ? (event.target ? event.target.value : event) : this.userSearchValue;
    this.userSearchValue = searchTerm || '';

    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredCustomerList = this.customerList;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredCustomerList = this.customerList.filter((customer) => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }

  saveMapping() {
    const { notif } = defaults;

    if (this.myForm.invalid) {
      this.notifSer.error('Error', 'Please fill all required fields', notif);
      return;
    }

    this.spinnerService.show();

    const formData: any = {
      ...this.myForm.value,
      _csrf: this.data._csrf
    };

    if (this.isEditMode) {
      formData._id = this.recordId;
      this.offrampService.updateOfframpAddressMapping(formData).subscribe(
        (response) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Address mapping updated successfully', notif);
            this.dialogRef.close({ success: true, data: response });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to update address mapping', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to update address mapping', notif);
        }
      );
    } else {
      this.offrampService.createOfframpAddressMapping(formData).subscribe(
        (response) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Address mapping created successfully', notif);
            this.dialogRef.close({ success: true, data: response });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to create address mapping', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to create address mapping', notif);
        }
      );
    }
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }
}



// WEBPACK FOOTER //
// ./src/app/offramp-address-mappings/add-offramp-address-mapping/add-offramp-address-mapping.component.ts